import React, { useCallback, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Layers,
  Loader2,
  Plus,
  PlusCircle,
  Printer,
  RotateCcw,
  SlidersHorizontal,
  StickyNote,
  Tag,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { showError, showWarning } from "@/store/errorStore";
import LabelItemCard from "./LabelItemCard";
import SheetLayoutDialog from "./SheetLayoutDialog";
import SheetMap, { itemColor } from "./SheetMap";
import { createLabelsPdf } from "./labelPdf";
import {
  DEFAULT_LAYOUT,
  LABELS_PER_SHEET,
  MAX_OFFSET_MM,
  SHEET,
  buildSheets,
  createEntry,
  hasDateConflict,
  isPrintable,
  todayIso,
} from "./labelSheet";

// Calibração da impressora fica no navegador (é do equipamento, não da empresa)
const LAYOUT_STORAGE_KEY = "etiquetas.layout";
const DATALIST_ID = "etiquetas-produtos";

const loadLayout = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(LAYOUT_STORAGE_KEY) || "{}");
    const offset = (n) =>
      Number.isFinite(n) ? Math.min(MAX_OFFSET_MM, Math.max(-MAX_OFFSET_MM, n)) : 0;
    return {
      offsetX: offset(saved.offsetX),
      offsetY: offset(saved.offsetY),
      showBorders: saved.showBorders === true,
    };
  } catch {
    return DEFAULT_LAYOUT;
  }
};

const saveLayout = (layout) => {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // Sem armazenamento (aba anônima): vale só nesta sessão
  }
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const focusField = (id) =>
  requestAnimationFrame(() => document.getElementById(id)?.focus());

/**
 * Gerador de etiquetas adesivas para folhas A4 Pimaco 65 (38,1 × 21,2 mm).
 * Tudo é local: monta a lista, distribui nas folhas e gera o PDF para imprimir.
 * `products` (carregado pelo App) alimenta a sugestão de nomes.
 */
function EtiqueLabelGenerator({ products = [] }) {
  const [entries, setEntries] = useState(() => [createEntry()]);
  const [startAt, setStartAt] = useState(0);
  const [layout, setLayout] = useState(loadLayout);
  const [showLayout, setShowLayout] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);

  const productNames = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const names = list.map((p) => String(p?.name || "").trim()).filter(Boolean);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [products]);

  const sheets = useMemo(() => buildSheets(entries, startAt), [entries, startAt]);
  const { pages, printed, pageCount, lastPageUsed } = sheets;
  const page = Math.min(previewPage, pageCount - 1);

  const percent = Math.round((lastPageUsed / LABELS_PER_SHEET) * 100);
  const blank = LABELS_PER_SHEET - lastPageUsed;
  const layoutCustomized =
    startAt > 0 || layout.offsetX !== 0 || layout.offsetY !== 0 || layout.showBorders;

  const printableItems = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => isPrintable(entry));

  /* ---------- Lista ---------- */

  const handleChange = useCallback((id, patch) => {
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  }, []);

  const handleRemove = useCallback((id) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const handleAdd = () => {
    const entry = createEntry();
    const last = entries[entries.length - 1];
    // Mesma fabricação do item anterior (produção do mesmo dia)
    if (last) {
      entry.useFabricationDate = last.useFabricationDate;
      entry.fabrication = last.fabrication;
      entry.fabricationText = last.fabricationText;
    }
    setEntries((prev) => [...prev, entry]);
    focusField(`label-${entry.id}-name`);
  };

  const handleReset = () => {
    const entry = createEntry();
    setEntries([entry]);
    setStartAt(0);
    setPreviewPage(0);
    setShowReset(false);
    focusField(`label-${entry.id}-name`);
  };

  const handleApplyLayout = ({ startAt: nextStart, layout: nextLayout }) => {
    setStartAt(nextStart);
    setLayout(nextLayout);
    saveLayout(nextLayout);
    setShowLayout(false);
  };

  /* ---------- PDF ---------- */

  const handlePrint = async () => {
    if (generating) return;

    const conflictIndex = entries.findIndex((e) => isPrintable(e) && hasDateConflict(e));
    if (conflictIndex >= 0) {
      showWarning(
        `No produto ${conflictIndex + 1}, a validade está antes da fabricação. Corrija as datas antes de imprimir.`,
        "Etiquetas"
      );
      focusField(`label-${entries[conflictIndex].id}-val`);
      return;
    }
    if (!printed) {
      showWarning(
        "Informe o nome e a quantidade de ao menos um produto para gerar as etiquetas.",
        "Etiquetas"
      );
      return;
    }

    // Abre a aba ainda no clique (depois do await o navegador bloquearia o pop-up)
    const win = window.open("", "_blank");
    if (win) {
      win.document.title = "Gerando etiquetas...";
      win.document.body.textContent = "Gerando etiquetas…";
    }

    setGenerating(true);
    try {
      const blob = await createLabelsPdf(pages, layout);
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `etiquetas-${todayIso()}.pdf`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("❌ Erro ao gerar o PDF de etiquetas:", error);
      win?.close();
      showError("Não foi possível gerar o PDF das etiquetas. Tente novamente.", "Etiquetas");
    } finally {
      setGenerating(false);
    }
  };

  const printButton = (className) => (
    <button
      type="button"
      onClick={handlePrint}
      disabled={generating}
      className={cn(
        "flex h-12 items-center justify-center gap-2.5 rounded-full bg-primary px-5 text-sm font-bold tracking-wide text-primary-foreground shadow-[0_8px_24px_rgba(27,94,32,0.18)] transition-all hover:bg-brand-dark active:scale-[0.98] disabled:opacity-70",
        className
      )}
    >
      {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Printer className="h-5 w-5" />}
      <span className="truncate">{generating ? "Gerando PDF..." : "Visualizar Impressão (PDF)"}</span>
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 pb-20 lg:pb-0">
      {/* Título */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="truncate font-display text-xl font-bold tracking-tight text-primary md:text-2xl">
            Etiquetas Adesivas
          </h1>
          <span className="flex-shrink-0 rounded-full bg-surface-container px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
            {plural(pageCount, "folha", "folhas")}
          </span>
        </div>
        <p className="truncate text-xs text-muted-foreground md:text-sm">
          Monte a folha A4 de 65 etiquetas e imprima em PDF
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        {/* Folha: capacidade, prévia e ações (coluna lateral no desktop) */}
        <aside className="space-y-4 lg:col-start-2 lg:row-start-1">
          <section className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                  <StickyNote className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h2 className="truncate font-display text-base font-bold leading-tight text-primary">
                    {SHEET.name}
                  </h2>
                  <p className="truncate text-[11px] text-muted-foreground">
                    Padrão 38,1 × 21,2 mm ({SHEET.columns} col. × {SHEET.rows} lin.)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowLayout(true)}
                className="relative flex flex-shrink-0 items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-primary transition-all hover:bg-surface-container active:scale-95"
              >
                <SlidersHorizontal className="h-3 w-3" />
                Layout
                {layoutCustomized && (
                  <span
                    className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-terra"
                    title="Layout personalizado"
                  />
                )}
              </button>
            </div>

            <div className="mt-3 space-y-1.5 border-t border-border/60 pt-3">
              <div className="flex items-end justify-between text-xs">
                <span className="font-medium text-muted-foreground">
                  {pageCount > 1 ? `Folha ${pageCount}: ` : "Preenchimento: "}
                  <strong className="font-bold text-primary">
                    {lastPageUsed}/{LABELS_PER_SHEET} etiquetas
                  </strong>
                </span>
                <span className="font-bold text-primary">{percent}%</span>
              </div>
              <div
                className="h-2.5 w-full overflow-hidden rounded-full border border-border/40 bg-surface-high p-0.5"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={LABELS_PER_SHEET}
                aria-valuenow={lastPageUsed}
                aria-label="Preenchimento da folha"
              >
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary to-brand-pistachio transition-all duration-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="flex justify-between gap-2 pt-0.5 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Tag className="h-2.5 w-2.5 text-primary" />
                  {printed} a imprimir
                  {startAt > 0 && ` · ${startAt} puladas`}
                </span>
                <span className="font-medium text-primary">
                  {plural(blank, "restante em branco", "restantes em branco")}
                </span>
              </div>
              {pageCount > 1 && (
                <p className="flex items-center gap-1.5 rounded-lg bg-surface-low px-2.5 py-1.5 text-[11px] font-medium text-foreground">
                  <Layers className="h-3.5 w-3.5 text-primary" />
                  A impressão usa {pageCount} folhas
                </p>
              )}
            </div>
          </section>

          {/* Prévia da folha (desktop) */}
          <section className="hidden rounded-2xl border border-border bg-card p-4 shadow-soft lg:block">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-sm font-bold text-foreground">Prévia da folha</h2>
              {pageCount > 1 && (
                <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => setPreviewPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    aria-label="Folha anterior"
                    className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span>
                    {page + 1} de {pageCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPreviewPage(Math.min(pageCount - 1, page + 1))}
                    disabled={page === pageCount - 1}
                    aria-label="Próxima folha"
                    className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <SheetMap slots={pages[page]} className="mx-auto w-full max-w-[260px]" />
            {printableItems.length > 0 && (
              <ul className="mt-3 space-y-1">
                {printableItems.map(({ entry, index }) => (
                  <li key={entry.id} className="flex items-center gap-2 text-xs">
                    <span className={cn("h-2.5 w-2.5 flex-shrink-0 rounded-sm", itemColor(index))} />
                    <span className="truncate text-foreground">
                      {index + 1}. {entry.name.trim()}
                    </span>
                    <span className="ml-auto flex-shrink-0 font-semibold text-primary">
                      {entry.quantity} un
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Ações (desktop) */}
          <div className="hidden gap-2 lg:flex">
            <button
              type="button"
              onClick={() => setShowReset(true)}
              title="Limpar tudo"
              aria-label="Limpar tudo"
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-border bg-surface text-primary transition-all hover:bg-surface-container active:scale-95"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            {printButton("flex-1")}
          </div>
        </aside>

        {/* Lista de produtos */}
        <section className="space-y-3.5 lg:col-start-1 lg:row-start-1">
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="min-w-0">
              <h2 className="font-display text-base font-bold text-foreground">Lista de Produtos</h2>
              <p className="truncate text-xs text-muted-foreground">Configure lote, datas e tiragem</p>
            </div>
            <button
              type="button"
              onClick={handleAdd}
              className="flex flex-shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-primary shadow-sm transition-all hover:border-brand-pistachio active:scale-95"
            >
              <Plus className="h-3 w-3" />
              Adicionar Item
            </button>
          </div>

          {entries.map((entry, index) => (
            <LabelItemCard
              key={entry.id}
              entry={entry}
              index={index}
              datalistId={DATALIST_ID}
              onChange={handleChange}
              onRemove={handleRemove}
            />
          ))}

          {entries.length === 0 && (
            <p className="px-1 text-center text-sm text-muted-foreground">
              Nenhum produto na lista. Adicione um para montar a folha.
            </p>
          )}

          <button
            type="button"
            onClick={handleAdd}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-card/60 py-3 text-xs font-bold text-primary transition-all hover:border-primary hover:bg-card active:scale-[0.99]"
          >
            <PlusCircle className="h-4 w-4" />
            Inserir Produto {entries.length + 1} na Folha
          </button>

          <datalist id={DATALIST_ID}>
            {productNames.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </section>
      </div>

      {/* Barra de ações (celular): acima da navegação inferior */}
      <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] z-20 border-t border-border bg-card/95 p-3 shadow-[0_-2px_12px_rgba(31,46,30,0.06)] backdrop-blur-md lg:hidden">
        <div className="mx-auto flex max-w-lg items-center gap-2.5">
          <button
            type="button"
            onClick={() => setShowReset(true)}
            title="Limpar tudo"
            aria-label="Limpar tudo"
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-border bg-surface text-primary transition-all hover:bg-surface-container active:scale-95"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          {printButton("min-w-0 flex-1")}
        </div>
      </div>

      <SheetLayoutDialog
        open={showLayout}
        onOpenChange={setShowLayout}
        entries={entries}
        startAt={startAt}
        layout={layout}
        onApply={handleApplyLayout}
      />

      <AlertDialog open={showReset} onOpenChange={setShowReset}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar a lista?</AlertDialogTitle>
            <AlertDialogDescription>
              Os produtos, quantidades e a posição inicial serão descartados. A calibração da
              impressora é mantida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Limpar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default EtiqueLabelGenerator;
